import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";

import {
  transformCronJob,
  transformCronJobExecution,
  transformCronJobExecutionLogRow,
  transformCronJobExecutionRow,
} from "./transformations";
import {
  CronJob,
  CronJobExecution,
  CronJobExecutionLogRow,
  CronJobExecutionLogSearchFormValues,
  CronJobExecutionRow,
  CronJobExecutionSearchFormValues,
} from "./types";

const BASE = "/v1/admin/cronjobs";

const errorOnly = {
  getMessage: { error: (error: any) => `${transformErrorResponse(error)}` },
};

/*---------------------------------------------------------------------------------------
    VRSTE OBRADA
-----------------------------------------------------------------------------------------*/
export const getCronJobsAPI = async (): Promise<CronJob[]> => {
  const response = await createApiCall({ path: BASE, method: "GET" }, errorOnly)();

  return (response.data?.results ?? []).map(transformCronJob);
};

export const getCronJobAPI = async (cronJobId: string): Promise<CronJob> => {
  const response = await createApiCall(
    { path: `${BASE}/${cronJobId}`, method: "GET" },
    errorOnly
  )();

  return transformCronJob(response.data);
};

/**
 * Ručni reset statusa vrste obrade na `finished`. Backend dopušta samo iz `running` i `error`,
 * a otvorenu obradu zatvara kao `aborted`.
 */
export const resetCronJobStatusAPI = async (cronJobId: string): Promise<CronJob> => {
  const response = await createApiCall(
    { path: `${BASE}/${cronJobId}/reset-status`, method: "POST" },
    {
      getMessage: {
        success: () => "Status vrste obrade vraćen na 'finished'.",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformCronJob(response.data);
};

/*---------------------------------------------------------------------------------------
    OBRADE
-----------------------------------------------------------------------------------------*/
export const searchCronJobExecutionsAPI = async (
  cronJobId: string,
  pagination: Pagination,
  filters?: CronJobExecutionSearchFormValues | null
): Promise<DataWithPagination<CronJobExecutionRow>> => {
  let path = `${BASE}/${cronJobId}/executions?`;
  if (filters?.status) path += `status=${encodeURIComponent(filters.status)}&`;

  return await paginatedApiCall(path, pagination, transformCronJobExecutionRow);
};

export const getCronJobExecutionAPI = async (executionId: number | string): Promise<CronJobExecution> => {
  const response = await createApiCall(
    { path: `${BASE}/executions/${executionId}`, method: "GET" },
    errorOnly
  )();

  return transformCronJobExecution(response.data);
};

/*---------------------------------------------------------------------------------------
    LOG OBRADE
-----------------------------------------------------------------------------------------*/
export const searchCronJobExecutionLogsAPI = async (
  executionId: number | string,
  pagination: Pagination,
  filters?: CronJobExecutionLogSearchFormValues | null
): Promise<DataWithPagination<CronJobExecutionLogRow>> => {
  let path = `${BASE}/executions/${executionId}/logs?`;
  if (filters?.search) path += `search=${encodeURIComponent(filters.search)}&`;

  return await paginatedApiCall(path, pagination, transformCronJobExecutionLogRow);
};
